// Cliente del modo Shorts "entorno" por COMPOSICIÓN (enfoque ganador):
//   1) START → dispara el job `video_shorts_compose` (backend) y devuelve un jobId.
//   2) STATUS → se polea hasta `done` (storage path) o `error`.
// El backend, por cada escena de ~7s, hace: Omni genera el fondo animado full-frame + la persona
// real recortada (Bria v3) va encima + subtítulos sticker karaoke + emojis temáticos + audio.
// La PLANTILLA (ej "pop") define todo el estilo. Tarda varios minutos (varias pasadas de Omni).
import { supabase } from './supabase';
import { atlasBackendFetch } from './atlasBackend';
import type { ShortsResult, ShortsStatus } from './shortsStudio';

export interface ShortsComposeParams {
  projectId: string;
  /** Signed URL (descargable) del video subido por el usuario. */
  sourceUrl: string;
  /** Nombre de la plantilla "entorno" (ej "pop") — define fondo + subtítulos + emojis. */
  template: string;
  /** 9:16 por defecto (vertical). */
  aspectRatio?: string;
  /** Duración del video en segundos (define el costo: 1 cr/s). */
  durationSec: number;
  /** Toggles del modo composición (Pop!/Titular). Default true. */
  subtitles?: boolean;
  emojis?: boolean;
}

const BUCKET = 'generadas'; // el resultado (output) vive en el bucket del historial (generadas/{company}/full)
const POLL_INTERVAL_MS = 4000;
// Pipeline lento (matting ~2-3min + fondos Omni + escenas + subtítulos) y si hay varios jobs
// encolados se SERIALIZAN (cola video) → el front acumula la espera de todos. Margen amplio para
// no rendirse antes de tiempo aunque el backend ya lo esté generando: 450 × 4s = 30 min.
const POLL_MAX_ATTEMPTS = 450;

function sleep(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms));
}

/** Costo en créditos: 1 por segundo del video total, mínimo 1 (espejo de `shortsComposeCredits`). */
export function creditsForShortCompose(durationSec: number): number {
  return Math.max(1, Math.ceil(durationSec));
}

/**
 * Dispara la generación del short "entorno" y polea hasta terminar. Devuelve el path + signed URL.
 * Lanza Error si falla o hace timeout. `onStatus` reporta transiciones.
 */
export async function generarShortCompose(
  params: ShortsComposeParams,
  onStatus?: (s: ShortsStatus) => void,
): Promise<ShortsResult> {
  const startBody = {
    project_id: params.projectId,
    source_url: params.sourceUrl,
    template: params.template,
    aspect_ratio: params.aspectRatio ?? '9:16',
    duration_sec: params.durationSec,
    subtitles: params.subtitles,
    emojis: params.emojis,
  };

  // ── 1) START ──────────────────────────────────────────────────────────
  const startRes = await atlasBackendFetch('/v1/videos/shorts-compose/start', startBody);
  if (!startRes.ok) {
    throw new Error(`No se pudo iniciar la generación (${startRes.status}).`);
  }
  const startRaw = await startRes.text();
  let startJson: { response?: string; jobId?: string; error?: string };
  try {
    startJson = JSON.parse(startRaw);
  } catch {
    throw new Error('Respuesta inválida del servidor.');
  }
  if (startJson.response !== 'success' || !startJson.jobId) {
    throw new Error(startJson.error || 'No se pudo iniciar la generación.');
  }
  const jobId = startJson.jobId;
  onStatus?.('processing');

  // ── 2) POLL STATUS ────────────────────────────────────────────────────
  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
    await sleep(POLL_INTERVAL_MS);
    let st: { status?: string; path?: string; error?: string };
    try {
      const stRes = await atlasBackendFetch('/v1/videos/shorts-compose/status', { job_id: jobId });
      if (!stRes.ok) continue;
      st = await stRes.json();
    } catch {
      continue;
    }

    if (st.status === 'done' && st.path) {
      const { data: urlData } = await supabase.storage.from(BUCKET).createSignedUrl(st.path, 3600);
      if (!urlData?.signedUrl) {
        throw new Error('Short generado pero no se pudo obtener la URL.');
      }
      onStatus?.('done');
      return { jobId, path: st.path, signedUrl: urlData.signedUrl };
    }
    if (st.status === 'error') {
      onStatus?.('error');
      throw new Error(st.error || 'La generación del short falló.');
    }
  }

  throw new Error('La generación tardó demasiado. Reintentá.');
}
